import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Search, Upload } from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import UploadModalCst from "@components/UploadModalCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL, downloadFile, uploadFile } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { WizardStepProps } from "@components/WizardCst/types";
import { StaticGroupWizardData } from "../types";
import { useStaticGroupWizardContext } from "../staticGroupWizardContext";
import { StepContainer } from "../../Wizard/components/StepLayout";

const PAGE_SIZE = 10;

const AddUsersStep: React.FC<WizardStepProps<StaticGroupWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("group-wizard-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const { groupId } = useStaticGroupWizardContext();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<string | null>("most-recent");
  const [csvOpen, setCsvOpen] = useState(false);

  const handleCsvUpload = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return uploadFile(
      `${ServicesURL.staticGroups}/static/${groupId}`,
      formData,
      MethodType.PUT,
    );
  };

  const listUrl = corporateId
    ? `${ServicesURL.getCustomer}?corporate_id=${corporateId}`
    : null;

  const { items, totalItems, totalPages, isLoading } = usePaginatedList(
    listUrl,
    { page, pageSize: PAGE_SIZE, search: search || undefined, sort },
  );

  const selected = new Set(data.userIds);

  const toggle = (userId: number, next: boolean) => {
    setData((prev) => {
      const set = new Set(prev.userIds);
      if (next) set.add(userId);
      else set.delete(userId);
      return { userIds: Array.from(set) };
    });
  };

  const sortOptions = [
    { label: t("sort.mostRecent"), value: "most-recent" },
    { label: t("sort.leastRecent"), value: "least-recent" },
    { label: t("sort.alphabetical"), value: "alphabetical" },
  ];

  return (
    <StepContainer>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 240,
            "& .MuiFormControl-root": {
              minWidth: "0 !important",
              width: "100% !important",
            },
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiFormHelperText-root": { display: "none" },
          }}
        >
          <InputCst
            id="group-users-search"
            label={t("search")}
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            endAdornment={
              <Search size={16} color={theme.palette.text.tertiary} />
            }
          />
        </Box>

        <Tooltip title={groupId ? "" : t("uploadCsvCreateHint")}>
          <span style={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              disabled={!groupId}
              onClick={() => setCsvOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                flexShrink: 0,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              }}
            >
              {t("uploadCsv")}
            </Button>
          </span>
        </Tooltip>

        <Box
          sx={{
            width: 180,
            "& > *": { minWidth: "0 !important", width: "100% !important" },
            "& .MuiFormControl-root": {
              minWidth: "0 !important",
              width: "100% !important",
            },
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          }}
        >
          <SelectCst
            id="group-users-sort"
            label={t("sort.mostRecent")}
            value={sort ?? ""}
            options={sortOptions}
            onChange={(next: any) => setSort(next || null)}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            color: theme.palette.text.tertiary,
          }}
        >
          {t("emptyUsers")}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((user: any) => {
            const fullName = [user.name, user.surname]
              .filter(Boolean)
              .join(" ")
              .trim();
            return (
              <ListItemCardCst
                key={user.id_user}
                title={fullName || user.email || String(user.id_user)}
                subtitle={{ id: user.id_user, activityType: user.email }}
                badges={
                  user.status === "active"
                    ? [{ label: t("active"), variant: "success" }]
                    : []
                }
                actions={[]}
                selectable
                selected={selected.has(user.id_user)}
                onSelect={(next) => toggle(user.id_user, next)}
              />
            );
          })}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </Box>
      )}

      <UploadModalCst
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        title={String(t("uploadCsv"))}
        accept=".csv,text/csv"
        banner={{
          title: String(t("csv-modal.banner-title")),
          text: String(t("csv-modal.banner-text")),
          downloadLabel: String(t("csv-modal.banner-download")),
          onDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/group/download/template`,
            ),
          secondaryDownloadLabel: String(
            t("csv-modal.banner-download-members"),
          ),
          onSecondaryDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/${groupId}/users/external-id`,
            ),
        }}
        onUpload={handleCsvUpload}
      />
    </StepContainer>
  );
};

export default AddUsersStep;
